import { useState, type FormEvent } from "react";
import {
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  ChevronDown,
  Church,
  ClipboardCheck,
  HeartHandshake,
  Loader2,
  LockKeyhole,
  Menu,
  ShieldCheck,
  Sparkles,
  Upload,
  Users,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { URLS } from "@/config/domains";

import { submitPilotAccessRequest } from "@/api/pilotAccess";

type ChurchSizeRange =
  | ""
  | "1-50"
  | "51-100"
  | "101-250"
  | "251-500"
  | "501-1000"
  | "1000+";

type PilotAccessForm = {
  churchName: string;
  churchCity: string;
  churchState: string;
  churchWebsite: string;
  churchSizeRange: ChurchSizeRange;
  estimatedMemberCount: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  currentSystem: string;
  primaryUseCase: string;
  preferredOnboardingTimeframe: string;
  notes: string;
  consent: boolean;
};

type FormErrors = Partial<Record<keyof PilotAccessForm, string>>;

const initialForm: PilotAccessForm = {
  churchName: "",
  churchCity: "",
  churchState: "",
  churchWebsite: "",
  churchSizeRange: "",
  estimatedMemberCount: "",
  contactFirstName: "",
  contactLastName: "",
  contactEmail: "",
  contactPhone: "",
  currentSystem: "",
  primaryUseCase: "",
  preferredOnboardingTimeframe: "",
  notes: "",
  consent: false,
};

const pilotBenefits = [
  {
    icon: Users,
    title: "Understand engagement",
    description:
      "See attendance patterns across members and households without relying on disconnected spreadsheets.",
  },
  {
    icon: HeartHandshake,
    title: "Strengthen follow-up",
    description:
      "Automatically surface members who may need care, encouragement, or a personal check-in.",
  },
  {
    icon: BarChart3,
    title: "Lead with clarity",
    description:
      "Give ministry leaders practical reporting and a clearer view of what needs attention.",
  },
];

const pilotIncludes = [
  "Guided church setup",
  "Member CSV migration",
  "Attendance and service tracking",
  "Automatic Care/Follow-up intelligence",
  "Foundational ministry reporting",
  "Direct access to the MinistryIQ pilot team",
];

export function PilotAccessRequestPage() {
  const [form, setForm] = useState<PilotAccessForm>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  function updateField<K extends keyof PilotAccessForm>(
    field: K,
    value: PilotAccessForm[K],
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((current) => ({
        ...current,
        [field]: undefined,
      }));
    }
  }

  function validateForm() {
    const nextErrors: FormErrors = {};

    if (!form.churchName.trim()) {
      nextErrors.churchName = "Church name is required.";
    }

    if (!form.churchCity.trim()) {
      nextErrors.churchCity = "City is required.";
    }

    if (!form.churchState.trim()) {
      nextErrors.churchState = "State is required.";
    }

    if (!form.contactFirstName.trim()) {
      nextErrors.contactFirstName = "First name is required.";
    }

    if (!form.contactLastName.trim()) {
      nextErrors.contactLastName = "Last name is required.";
    }

    if (!form.contactEmail.trim()) {
      nextErrors.contactEmail = "Work email is required.";
    } else if (!isValidEmail(form.contactEmail)) {
      nextErrors.contactEmail = "Enter a valid email address.";
    }

    if (!form.churchSizeRange) {
      nextErrors.churchSizeRange = "Select a church size.";
    }

    if (!form.primaryUseCase.trim()) {
      nextErrors.primaryUseCase =
        "Tell us what you would like MinistryIQ to help improve.";
    }

    if (!form.consent) {
      nextErrors.consent =
        "You must agree to be contacted about the MinistryIQ pilot.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!validateForm()) {
      scrollToFirstError();
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      await submitPilotAccessRequest({
        church_name: form.churchName.trim(),
        church_city: form.churchCity.trim(),
        church_state: form.churchState,
        church_website: emptyToNull(form.churchWebsite),
        church_size_range: form.churchSizeRange || null,
        estimated_member_count: form.estimatedMemberCount
          ? Number(form.estimatedMemberCount)
          : null,
        contact_first_name: form.contactFirstName.trim(),
        contact_last_name: form.contactLastName.trim(),
        contact_email: form.contactEmail.trim(),
        contact_phone: emptyToNull(form.contactPhone),
        current_system: emptyToNull(form.currentSystem),
        primary_use_case: emptyToNull(form.primaryUseCase),
        notes: buildSubmissionNotes(
          form.notes,
          form.preferredOnboardingTimeframe,
        ),
        source: "WEBSITE",
      });

      setIsSubmitted(true);
      setForm(initialForm);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to submit your request. Please try again.";

      setSubmissionError(message);
      window.requestAnimationFrame(() => {
        document
          .getElementById("pilot-request-submission-error")
          ?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSubmitted) {
    return <PilotRequestSubmitted />;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-950 text-white">
      <PublicHeader
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={() => setMobileMenuOpen((current) => !current)}
        onCloseMobileMenu={() => setMobileMenuOpen(false)}
      />

      <main>
        <section className="relative overflow-hidden border-b border-slate-800/80">
          <div className="absolute inset-0">
            <div className="absolute left-[-10rem] top-[-8rem] h-[32rem] w-[32rem] rounded-full bg-indigo-600/15 blur-3xl" />
            <div className="absolute right-[-12rem] top-[6rem] h-[34rem] w-[34rem] rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b33_1px,transparent_1px),linear-gradient(to_bottom,#1e293b33_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_82%)]" />
          </div>

          <div className="relative mx-auto grid max-w-7xl gap-14 px-6 pb-20 pt-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-8 lg:pb-28 lg:pt-24">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-indigo-400/20 bg-indigo-500/10 px-4 py-2 text-sm font-semibold text-indigo-200">
                <Sparkles size={15} />
                Now accepting a limited number of pilot churches
              </div>

              <h1 className="mt-7 max-w-4xl text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl lg:leading-[1.08]">
                See the people behind the{" "}
                <span className="bg-gradient-to-r from-indigo-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent">
                  attendance numbers.
                </span>
              </h1>

              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">
                MinistryIQ helps church leaders understand engagement, recognize
                when members may need care, and make better ministry decisions
                with greater clarity.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="#request-access"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 transition hover:bg-indigo-400"
                >
                  Request Pilot Access
                  <ArrowRight size={17} />
                </a>

                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900/70 px-6 py-3.5 text-sm font-semibold text-slate-200 transition hover:border-slate-600 hover:bg-slate-900"
                >
                  See how the pilot works
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-400">
                <TrustItem label="No public sign-up required" />
                <TrustItem label="Guided onboarding" />
                <TrustItem label="Secure church workspace" />
              </div>
            </div>

            <PilotPreviewCard />
          </div>
        </section>

        <section className="border-b border-slate-800 bg-slate-950">
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-indigo-400">
                Built for practical ministry
              </p>

              <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                Move from scattered information to meaningful action.
              </h2>

              <p className="mt-5 text-lg leading-8 text-slate-400">
                MinistryIQ brings attendance, member engagement, care, and
                reporting together in one focused platform.
              </p>
            </div>

            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {pilotBenefits.map((benefit) => {
                const Icon = benefit.icon;

                return (
                  <article
                    key={benefit.title}
                    className="group rounded-2xl border border-slate-800 bg-slate-900/55 p-7 transition duration-200 hover:-translate-y-1 hover:border-indigo-500/30 hover:bg-slate-900"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/12 text-indigo-300 ring-1 ring-indigo-500/20">
                      <Icon size={23} />
                    </div>

                    <h3 className="mt-6 text-xl font-semibold text-white">
                      {benefit.title}
                    </h3>

                    <p className="mt-3 leading-7 text-slate-400">
                      {benefit.description}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className="border-b border-slate-800 bg-slate-900/25"
        >
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
            <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-400">
                  The pilot experience
                </p>

                <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                  A guided start—not another system your church must figure out
                  alone.
                </h2>

                <p className="mt-6 max-w-xl text-lg leading-8 text-slate-400">
                  We are partnering with a small group of churches so we can
                  provide direct onboarding support and learn from real ministry
                  workflows.
                </p>

                <div className="mt-9 space-y-5">
                  <ProcessStep
                    number="01"
                    title="Submit your request"
                    description="Tell us about your church, current process, and the ministry challenges you want to improve."
                  />

                  <ProcessStep
                    number="02"
                    title="Pilot review"
                    description="The MinistryIQ team reviews your request and contacts you about fit, timing, and next steps."
                  />

                  <ProcessStep
                    number="03"
                    title="Receive your invitation"
                    description="Approved churches receive a secure invitation to create their MinistryIQ workspace."
                  />

                  <ProcessStep
                    number="04"
                    title="Complete guided onboarding"
                    description="Confirm your church profile, import member data, configure services, and begin using MinistryIQ."
                  />
                </div>
              </div>

              <div className="rounded-3xl border border-slate-800 bg-slate-900/80 p-7 shadow-2xl shadow-black/25 sm:p-9">
                <div className="flex items-center gap-4 border-b border-slate-800 pb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-300">
                    <ClipboardCheck size={24} />
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold">
                      Your pilot includes
                    </h3>
                    <p className="mt-1 text-sm text-slate-400">
                      The essentials needed to launch successfully
                    </p>
                  </div>
                </div>

                <div className="mt-7 grid gap-4 sm:grid-cols-2">
                  {pilotIncludes.map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-950/60 p-4"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                        <Check size={13} strokeWidth={3} />
                      </span>

                      <span className="text-sm leading-6 text-slate-300">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-7 rounded-2xl border border-indigo-500/20 bg-indigo-500/8 p-5">
                  <div className="flex gap-3">
                    <ShieldCheck
                      className="mt-0.5 shrink-0 text-indigo-300"
                      size={22}
                    />

                    <div>
                      <p className="font-semibold text-indigo-100">
                        Invite-only by design
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate-400">
                        Submitting this form does not automatically create an
                        account. Approved churches receive a secure,
                        time-limited invitation from the MinistryIQ team.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="request-access"
          className="relative overflow-hidden bg-slate-950"
        >
          <div className="absolute bottom-[-18rem] right-[-12rem] h-[38rem] w-[38rem] rounded-full bg-indigo-600/10 blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-[0.72fr_1.28fr]">
              <div className="lg:sticky lg:top-10 lg:self-start">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-indigo-400">
                  Request access
                </p>

                <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                  Bring MinistryIQ to your church.
                </h2>

                <p className="mt-6 text-lg leading-8 text-slate-400">
                  Tell us a little about your church and what you hope to
                  improve. We will review your request and contact you with next
                  steps.
                </p>

                <div className="mt-8 space-y-4">
                  <SideNote
                    icon={LockKeyhole}
                    title="Your information is protected"
                    description="Request information is used only to evaluate and coordinate MinistryIQ pilot participation."
                  />

                  <SideNote
                    icon={Upload}
                    title="Migration support included"
                    description="Approved pilot churches can import existing member information using MinistryIQ's guided CSV migration."
                  />

                  <SideNote
                    icon={Church}
                    title="Designed around your church"
                    description="Each approved church receives its own secure, isolated MinistryIQ workspace."
                  />
                </div>
              </div>

              <form
                noValidate
                onSubmit={handleSubmit}
                className="rounded-3xl border border-slate-800 bg-slate-900/80 p-6 shadow-2xl shadow-black/30 sm:p-8 lg:p-10"
              >
                {submissionError && (
                  <div
                    id="pilot-request-submission-error"
                    role="alert"
                    className="mb-8 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm leading-6 text-rose-200"
                  >
                    {submissionError}
                  </div>
                )}

                <FormSection
                  eyebrow="Step 1"
                  title="Tell us about your church"
                  description="This helps us understand your church's size and current setup."
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <TextField
                        id="church-name"
                        label="Church name"
                        required
                        placeholder="New Hope Community Church"
                        value={form.churchName}
                        error={errors.churchName}
                        onChange={(value) => updateField("churchName", value)}
                      />
                    </div>

                    <TextField
                      id="church-city"
                      label="City"
                      required
                      placeholder="Waldorf"
                      value={form.churchCity}
                      error={errors.churchCity}
                      onChange={(value) => updateField("churchCity", value)}
                    />

                    <SelectField
                      id="church-state"
                      label="State"
                      required
                      value={form.churchState}
                      error={errors.churchState}
                      onChange={(value) => updateField("churchState", value)}
                    >
                      <option value="">Select a state</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="DC">District of Columbia</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                    </SelectField>

                    <TextField
                      id="church-website"
                      label="Church website"
                      placeholder="https://yourchurch.org"
                      value={form.churchWebsite}
                      error={errors.churchWebsite}
                      onChange={(value) => updateField("churchWebsite", value)}
                    />

                    <SelectField
                      id="church-size"
                      label="Church size"
                      required
                      value={form.churchSizeRange}
                      error={errors.churchSizeRange}
                      onChange={(value) =>
                        updateField(
                          "churchSizeRange",
                          value as ChurchSizeRange,
                        )
                      }
                    >
                      <option value="">Select a range</option>
                      <option value="1-50">1—50 people</option>
                      <option value="51-100">51—100 people</option>
                      <option value="101-250">101—250 people</option>
                      <option value="251-500">251—500 people</option>
                      <option value="501-1000">501—1,000 people</option>
                      <option value="1000+">More than 1,000 people</option>
                    </SelectField>

                    <TextField
                      id="estimated-members"
                      label="Estimated member records"
                      type="number"
                      min="0"
                      placeholder="325"
                      value={form.estimatedMemberCount}
                      error={errors.estimatedMemberCount}
                      onChange={(value) =>
                        updateField("estimatedMemberCount", value)
                      }
                    />

                    <SelectField
                      id="current-system"
                      label="Current system"
                      value={form.currentSystem}
                      error={errors.currentSystem}
                      onChange={(value) =>
                        updateField("currentSystem", value)
                      }
                    >
                      <option value="">Select your current system</option>
                      <option value="Spreadsheets">Spreadsheets</option>
                      <option value="Breeze">Breeze</option>
                      <option value="Planning Center">
                        Planning Center
                      </option>
                      <option value="Church Community Builder">
                        Church Community Builder
                      </option>
                      <option value="Realm">Realm</option>
                      <option value="Rock RMS">Rock RMS</option>
                      <option value="Other church management system">
                        Other church management system
                      </option>
                      <option value="No current system">
                        No current system
                      </option>
                    </SelectField>
                  </div>
                </FormSection>

                <FormDivider />

                <FormSection
                  eyebrow="Step 2"
                  title="Primary contact"
                  description="This person will receive updates about the pilot request."
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <TextField
                      id="contact-first-name"
                      label="First name"
                      required
                      placeholder="James"
                      value={form.contactFirstName}
                      error={errors.contactFirstName}
                      onChange={(value) =>
                        updateField("contactFirstName", value)
                      }
                    />

                    <TextField
                      id="contact-last-name"
                      label="Last name"
                      required
                      placeholder="Williams"
                      value={form.contactLastName}
                      error={errors.contactLastName}
                      onChange={(value) =>
                        updateField("contactLastName", value)
                      }
                    />

                    <TextField
                      id="contact-email"
                      label="Work email"
                      required
                      type="email"
                      placeholder="james@yourchurch.org"
                      value={form.contactEmail}
                      error={errors.contactEmail}
                      onChange={(value) =>
                        updateField("contactEmail", value)
                      }
                    />

                    <TextField
                      id="contact-phone"
                      label="Phone number"
                      type="tel"
                      placeholder="301-555-0100"
                      value={form.contactPhone}
                      error={errors.contactPhone}
                      onChange={(value) =>
                        updateField("contactPhone", value)
                      }
                    />
                  </div>
                </FormSection>

                <FormDivider />

                <FormSection
                  eyebrow="Step 3"
                  title="What would you like to improve?"
                  description="Help us understand where MinistryIQ could have the greatest impact."
                >
                  <div className="space-y-5">
                    <TextAreaField
                      id="primary-use-case"
                      label="Primary goal or challenge"
                      required
                      rows={5}
                      placeholder="For example: We want a clearer way to track attendance trends and know when a member may need follow-up."
                      value={form.primaryUseCase}
                      error={errors.primaryUseCase}
                      onChange={(value) =>
                        updateField("primaryUseCase", value)
                      }
                    />

                    <SelectField
                      id="onboarding-timeframe"
                      label="Preferred onboarding timeframe"
                      value={form.preferredOnboardingTimeframe}
                      error={errors.preferredOnboardingTimeframe}
                      onChange={(value) =>
                        updateField("preferredOnboardingTimeframe", value)
                      }
                    >
                      <option value="">No preference</option>
                      <option value="As soon as possible">
                        As soon as possible
                      </option>
                      <option value="Within 30 days">Within 30 days</option>
                      <option value="Within 60 days">Within 60 days</option>
                      <option value="Within 90 days">Within 90 days</option>
                      <option value="Just exploring">Just exploring</option>
                    </SelectField>

                    <TextAreaField
                      id="additional-notes"
                      label="Additional notes"
                      rows={4}
                      placeholder="Anything else you would like us to know?"
                      value={form.notes}
                      error={errors.notes}
                      onChange={(value) => updateField("notes", value)}
                    />

                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-800 bg-slate-950/55 p-4">
                      <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={(event) =>
                          updateField("consent", event.target.checked)
                        }
                        className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-indigo-500 focus:ring-indigo-500"
                      />

                      <span>
                        <span className="text-sm leading-6 text-slate-300">
                          I agree that MinistryIQ may contact me regarding this
                          pilot access request.
                          <span className="ml-1 text-rose-400">*</span>
                        </span>

                        {errors.consent && (
                          <span className="mt-1 block text-sm text-rose-400">
                            {errors.consent}
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                </FormSection>

                <div className="mt-8 flex flex-col gap-4 border-t border-slate-800 pt-7 sm:flex-row sm:items-center sm:justify-between">
                  <p className="max-w-md text-xs leading-5 text-slate-500">
                    Submitting a request does not guarantee pilot acceptance or
                    create a MinistryIQ account.
                  </p>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-indigo-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={17} />
                        Submitting
                      </>
                    ) : (
                      <>
                        Request Pilot Access
                        <ArrowRight size={17} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

function PublicHeader({
  mobileMenuOpen,
  onToggleMobileMenu,
  onCloseMobileMenu,
}: {
  mobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
  onCloseMobileMenu: () => void;
}) {
  return (
    <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/90 backdrop-blur-xl">
      <div className="mx-auto flex h-30 max-w-7xl items-center justify-between px-6 lg:px-8">
        <Link
          to="/"
          aria-label="MinistryIQ home"
          className="flex shrink-0 items-center py-2"
        >
          <img
            src="/official_logo.png"
            alt="MinistryIQ — See More. Care Better. Lead Smarter."
            className="h-44 w-[300px] object-contain lg:w-[340px]"
          />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <a
            href="#how-it-works"
            className="text-sm font-medium text-slate-400 transition hover:text-white"
          >
            How it works
          </a>

          <a
            href="#request-access"
            className="text-sm font-medium text-slate-400 transition hover:text-white"
          >
            Request access
          </a>

          <Link
            to={`${URLS.church}/app/login`}
            className="text-sm font-medium text-slate-400 transition hover:text-white"
          >
            Sign in
          </Link>

          <a
            href="#request-access"
            className="rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-400"
          >
            Join the pilot
          </a>
        </nav>

        <button
          type="button"
          onClick={onToggleMobileMenu}
          className="rounded-lg border border-slate-800 p-2 text-slate-300 md:hidden"
          aria-label="Toggle navigation"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? (
            <X size={21} />
          ) : (
            <Menu size={21} />
          )}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="border-t border-slate-800 bg-slate-950 px-6 py-5 md:hidden">
          <nav className="flex flex-col gap-2">
            <MobileNavLink
              href="#how-it-works"
              label="How it works"
              onClick={onCloseMobileMenu}
            />

            <MobileNavLink
              href="#request-access"
              label="Request access"
              onClick={onCloseMobileMenu}
            />

            <Link
              to={`${URLS.church}/app/login`}
              onClick={onCloseMobileMenu}
              className="rounded-lg px-3 py-3 text-sm font-medium text-slate-300 hover:bg-slate-900"
            >
              Sign in
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

function PilotPreviewCard() {
  return (
    <div className="relative mx-auto w-full max-w-xl">
      <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-indigo-500/15 to-emerald-500/10 blur-2xl" />

      <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/95 shadow-2xl shadow-black/40">
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-300">
              <Church size={21} />
            </div>

            <div>
              <p className="font-semibold text-white">Ministry overview</p>
              <p className="text-xs text-slate-500">
                A clearer view of church engagement
              </p>
            </div>
          </div>

          <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
            Pilot
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 p-6">
          <PreviewMetric
            label="Members tracked"
            value="428"
            change="+12 this month"
          />

          <PreviewMetric
            label="Attendance trend"
            value="82%"
            change="Stable engagement"
          />

          <PreviewMetric
            label="Care follow-ups"
            value="7"
            change="Need attention"
          />

          <PreviewMetric
            label="Households"
            value="164"
            change="Connected profiles"
          />
        </div>

        <div className="border-t border-slate-800 p-6">
          <div className="rounded-2xl border border-amber-500/15 bg-amber-500/[0.06] p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-300">
                <HeartHandshake size={20} />
              </div>

              <div>
                <p className="font-semibold text-white">
                  Follow-up opportunity identified
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  MinistryIQ detected an attendance pattern that may benefit
                  from a friendly personal check-in.
                </p>

                <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-indigo-300">
                  Review recommended action
                  <ArrowRight size={15} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewMetric({
  label,
  value,
  change,
}: {
  label: string;
  value: string;
  change: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/65 p-5">
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-500">
        {label}
      </p>
      <p className="mt-3 text-3xl font-bold tracking-tight text-white">
        {value}
      </p>
      <p className="mt-2 text-xs text-slate-500">{change}</p>
    </div>
  );
}

function TrustItem({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/12 text-emerald-400">
        <Check size={12} strokeWidth={3} />
      </span>
      {label}
    </div>
  );
}

function ProcessStep({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-sm font-bold text-indigo-300">
        {number}
      </div>

      <div>
        <h3 className="font-semibold text-white">{title}</h3>
        <p className="mt-1.5 leading-7 text-slate-400">{description}</p>
      </div>
    </div>
  );
}

function SideNote({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof ShieldCheck;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4 rounded-2xl border border-slate-800 bg-slate-900/45 p-5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-300">
        <Icon size={20} />
      </div>

      <div>
        <p className="font-semibold text-white">{title}</p>
        <p className="mt-1.5 text-sm leading-6 text-slate-400">
          {description}
        </p>
      </div>
    </div>
  );
}

function FormSection({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-indigo-400">
        {eyebrow}
      </p>
      <h3 className="mt-2 text-xl font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-400">{description}</p>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function FormDivider() {
  return <div className="my-8 border-t border-slate-800" />;
}

function TextField({
  id,
  label,
  required = false,
  type = "text",
  placeholder,
  value,
  error,
  min,
  onChange,
}: {
  id: string;
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  value: string;
  error?: string;
  min?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id} label={label} required={required} />

      <input
        id={id}
        type={type}
        min={min}
        value={value}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={fieldClass(error)}
      />

      {error && <FieldError id={`${id}-error`} message={error} />}
    </div>
  );
}

function SelectField({
  id,
  label,
  required = false,
  value,
  error,
  onChange,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id} label={label} required={required} />

      <div className="relative">
        <select
          id={id}
          value={value}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => onChange(event.target.value)}
          className={`${fieldClass(error)} appearance-none pr-11`}
        >
          {children}
        </select>

        <ChevronDown
          size={17}
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
        />
      </div>

      {error && <FieldError id={`${id}-error`} message={error} />}
    </div>
  );
}

function TextAreaField({
  id,
  label,
  required = false,
  rows,
  placeholder,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  required?: boolean;
  rows: number;
  placeholder?: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id} label={label} required={required} />

      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`${fieldClass(error)} resize-y`}
      />

      {error && <FieldError id={`${id}-error`} message={error} />}
    </div>
  );
}

function FieldLabel({
  htmlFor,
  label,
  required,
}: {
  htmlFor: string;
  label: string;
  required: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-sm font-medium text-slate-300"
    >
      {label}
      {required && <span className="ml-1 text-rose-400">*</span>}
    </label>
  );
}

function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p id={id} className="mt-2 text-sm text-rose-400">
      {message}
    </p>
  );
}

function fieldClass(error?: string) {
  return [
    "w-full rounded-xl border bg-slate-950/75 px-4 py-3 text-sm text-white",
    "outline-none transition placeholder:text-slate-600",
    "focus:ring-2 focus:ring-indigo-500/20",
    error
      ? "border-rose-500/60 focus:border-rose-400"
      : "border-slate-700 focus:border-indigo-400",
  ].join(" ");
}

function PilotRequestSubmitted() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-6 py-16 text-white">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-[-16rem] h-[40rem] w-[40rem] -translate-x-1/2 rounded-full bg-indigo-600/15 blur-3xl" />
      </div>

      <div className="relative w-full max-w-2xl rounded-3xl border border-slate-800 bg-slate-900/90 p-8 text-center shadow-2xl shadow-black/40 sm:p-12">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/12 text-emerald-400 ring-1 ring-emerald-500/20">
          <CheckCircle2 size={34} />
        </div>

        <p className="mt-7 text-sm font-semibold uppercase tracking-[0.2em] text-emerald-400">
          Request received
        </p>

        <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
          Thank you for your interest in MinistryIQ.
        </h1>

        <p className="mx-auto mt-5 max-w-xl text-lg leading-8 text-slate-400">
          The MinistryIQ team will review your pilot access request and contact
          you with next steps. No account has been created yet.
        </p>

        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-950/65 p-5 text-left">
          <p className="font-semibold text-white">What happens next?</p>

          <div className="mt-4 space-y-3 text-sm leading-6 text-slate-400">
            <p>1. We review your church's needs and pilot readiness.</p>
            <p>2. A MinistryIQ team member contacts your primary contact.</p>
            <p>
              3. Approved churches receive a secure invitation to create their
              workspace.
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link
            to={`${URLS.church}/app/login`}
            className="inline-flex items-center justify-center rounded-xl border border-slate-700 px-5 py-3 text-sm font-semibold text-slate-300 transition hover:bg-slate-800 hover:text-white"
          >
            MinistryIQ sign in
          </Link>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-xl bg-indigo-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-400"
          >
            Submit another request
          </button>
        </div>
      </div>
    </div>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-slate-800 bg-slate-950">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between lg:px-8">
        <Link
          to="/"
          aria-label="MinistryIQ home"
          className="inline-flex shrink-0 items-center"
        >
          
        </Link>

        <div className="text-sm text-slate-500">
          © {new Date().getFullYear()} MinistryIQ. Pilot access is
          currently invite-only.
        </div>
      </div>
    </footer>
  );
}

function MobileNavLink({
  href,
  label,
  onClick,
}: {
  href: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="rounded-lg px-3 py-3 text-sm font-medium text-slate-300 hover:bg-slate-900"
    >
      {label}
    </a>
  );
}

function emptyToNull(value: string) {
  const cleaned = value.trim();
  return cleaned || null;
}

function buildSubmissionNotes(
  notes: string,
  preferredOnboardingTimeframe: string,
) {
  const sections: string[] = [];

  if (preferredOnboardingTimeframe.trim()) {
    sections.push(
      `Preferred onboarding timeframe: ${preferredOnboardingTimeframe.trim()}`,
    );
  }

  if (notes.trim()) {
    sections.push(notes.trim());
  }

  return sections.length > 0 ? sections.join("\n\n") : null;
}


function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function scrollToFirstError() {
  window.requestAnimationFrame(() => {
    const firstInvalidField = document.querySelector<HTMLElement>(
      '[aria-invalid="true"]',
    );

    firstInvalidField?.focus();
    firstInvalidField?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  });
}